import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import * as React from 'react';
import { buttonVariants } from '@/components/ui/button-variants';
import { cn } from '@/lib/utils';

/**
 * Confirmation for destructive actions.
 *
 * An AlertDialog, not a Dialog. The difference is not cosmetic: AlertDialog
 * traps focus on the *cancel* button by default and cannot be dismissed by
 * clicking the overlay. Both are deliberate — the safe option should be the one
 * you get by reflexively hitting Enter or Escape, and a stray click should never
 * be able to delete something.
 */

const AlertDialog = AlertDialogPrimitive.Root;
const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /**
   * Say what will actually happen, in concrete terms.
   *
   * "Are you sure?" tells the user nothing they don't already know. The copy
   * that matters is the consequence they *haven't* thought of — e.g. that
   * deleting a published post here does not delete it from X.
   */
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  onConfirm: () => void | Promise<void>;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  loading = false,
  onConfirm,
}: ConfirmDialogProps) {
  const [pending, setPending] = React.useState(false);
  const busy = loading || pending;

  const handleConfirm = async (event: React.MouseEvent) => {
    // Keep the dialog open while the request is in flight, so the user sees the
    // spinner rather than the dialog vanishing and nothing appearing to happen.
    event.preventDefault();
    setPending(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } finally {
      setPending(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-black/60 backdrop-blur-sm',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
          )}
        />
        <AlertDialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2',
            'bg-card rounded-xl border p-6 shadow-2xl duration-200',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-96',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-96',
          )}
        >
          <AlertDialogPrimitive.Title className="text-lg font-semibold tracking-tight">
            {title}
          </AlertDialogPrimitive.Title>

          <AlertDialogPrimitive.Description className="text-muted-foreground mt-2 text-sm leading-relaxed">
            {description}
          </AlertDialogPrimitive.Description>

          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <AlertDialogPrimitive.Cancel
              disabled={busy}
              className={cn(buttonVariants({ variant: 'outline', size: 'sm' }))}
            >
              {cancelLabel}
            </AlertDialogPrimitive.Cancel>

            <AlertDialogPrimitive.Action
              onClick={handleConfirm}
              disabled={busy}
              className={cn(
                buttonVariants({ variant: destructive ? 'destructive' : 'default', size: 'sm' }),
              )}
            >
              {busy ? 'Working…' : confirmLabel}
            </AlertDialogPrimitive.Action>
          </div>
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialog>
  );
}

export { AlertDialog, AlertDialogTrigger };
